/**
 * Which weights Review Assist runs, pinned.
 *
 * Its own module so that ModelRunner can name the model without importing the
 * worker — a static import of assist.worker.ts would pull Transformers.js into
 * the main bundle and make every visitor pay for a feature most never enable.
 *
 * DINOv2-small, converted to ONNX by onnx-community, 4-bit weights with 16-bit
 * activations: 14.9 MB on the wire for a 384-number vector per frame.
 *
 * Pinned to a commit rather than `main` on purpose. A silent upstream reconvert
 * would change every photographer's burst boundaries overnight, with no version
 * bump and nothing in the interface to explain why yesterday's shoot groups
 * differently today. Changing these lines is a deliberate act, and the `model`
 * column in media_assist invalidates stored vectors when it happens.
 */

export const MODEL_ID = 'onnx-community/dinov2-small-ONNX';
export const MODEL_REVISION = '08c606e3123472a388efa59181b677d428f69bbd';
/**
 * The quantisation depends on where it runs, and that is not a preference.
 *
 * q4f16 keeps half-precision activations, which WebGPU handles natively and the
 * WASM backend cannot execute at all — it fails at session creation with a
 * layer-norm fusion error, not at inference, so there is no graceful degradation
 * to fall back on. q4 is the same 4-bit weights with 32-bit activations: 2 MB
 * larger, and it runs anywhere.
 */
export const dtypeFor = (device: 'webgpu' | 'wasm') => (device === 'webgpu' ? 'q4f16' : 'q4');

/**
 * Written into every row this model produces.
 *
 * Deliberately does NOT include the quantisation. The same laptop can land on
 * WebGPU one day and WASM the next — a driver update, a browser flag, a
 * blocklist — and tagging by dtype would silently re-embed a three-thousand
 * frame shoot because of it.
 *
 * Measured, not assumed: the same pair of bundled photographs scores 0.2410 on
 * WASM/q4 and 0.2408 on WebGPU/q4f16 (tests/e2e/model.mjs, both paths). A
 * disagreement of 0.0002 against a burst threshold in the second decimal place.
 * Mixing the two within one shoot is not a problem worth hours of someone's GPU.
 */
export const MODEL_TAG = 'dinov2-small@q4';

/**
 * The ONNX Runtime build the worker loads, pinned to the version installed in
 * package.json. Kept beside the model pin because the pair of them is what
 * makes a review reproducible.
 */
export const ORT_VERSION = '1.26.0-dev.20260416-b7804b056c';
